import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Banknote, CalendarCheck2, ChevronLeft, ChevronRight, Clock3, Download, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const SERVICIOS = ["Evaluación", "Terapia individual", "Terapia de parejas", "Terapia parental", "Teleconsulta", "Domicilio", "Otro"];

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const [form, setForm] = useState({ fecha: todayStr(), paciente: "", servicio: "", monto: "", estado: "pagada", notas: "" });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/appointments");
      setItems(res.data);
    } catch {
      toast.error("No se pudieron cargar las citas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const monthItems = useMemo(() => items.filter((a) => a.fecha?.startsWith(month)), [items, month]);
  const ingresos = monthItems.filter((a) => a.estado === "pagada").reduce((s, a) => s + a.monto, 0);
  const pendiente = monthItems.filter((a) => a.estado === "pendiente").reduce((s, a) => s + a.monto, 0);
  const rawLabel = new Date(`${month}-02T12:00:00`).toLocaleDateString("es", { month: "long", year: "numeric" });
  const monthLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const resetForm = () => {
    setForm({ fecha: todayStr(), paciente: "", servicio: "", monto: "", estado: "pagada", notas: "" });
    setEditingId(null);
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({ fecha: a.fecha, paciente: a.paciente, servicio: a.servicio, monto: String(a.monto), estado: a.estado, notas: a.notas || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/appointments/export?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finanzas-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Finanzas del mes exportadas en CSV");
    } catch {
      toast.error("No se pudo exportar");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.servicio) {
      toast.error("Selecciona el servicio");
      return;
    }
    setSaving(true);
    try {
      const body = {
        fecha: form.fecha,
        paciente: form.paciente.trim(),
        servicio: form.servicio,
        monto: Number(form.monto),
        estado: form.estado,
        notas: form.notas.trim() || null,
      };
      if (editingId) {
        await api.put(`/appointments/${editingId}`, body);
        toast.success("Cita actualizada");
      } else {
        await api.post("/appointments", body);
        toast.success("Cita registrada");
      }
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      setItems((prev) => prev.filter((a) => a.id !== id));
      toast.success("Cita eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid="appointments-tab">
      <div className="flex items-center justify-between bg-white border border-line rounded-lg px-5 py-3">
        <Button variant="ghost" size="sm" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="month-prev-button" className="hover:bg-sand hover:text-ink">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <p className="font-serif text-lg" data-testid="month-label">{monthLabel}</p>
        <Button variant="ghost" size="sm" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="month-next-button" className="hover:bg-sand hover:text-ink">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Banknote, label: "Ingresos cobrados", value: money(ingresos), testid: "stat-month-income" },
          { icon: CalendarCheck2, label: "Citas del mes", value: monthItems.length, testid: "stat-month-appointments" },
          { icon: Clock3, label: "Pendiente por cobrar", value: money(pendiente), testid: "stat-month-pending" },
        ].map(({ icon: Icon, label, value, testid }) => (
          <div key={testid} className="bg-white border border-line rounded-lg p-5 flex items-center gap-4" data-testid={testid}>
            <span className="grid place-items-center h-11 w-11 rounded-full bg-sand border border-line">
              <Icon className="h-5 w-5 text-sage" />
            </span>
            <div>
              <p className="font-serif text-3xl leading-none">{value}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-stonemute mt-1">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} data-testid="appointment-form" className="bg-white border border-line rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-lg">{editingId ? "Editar cita" : "Registrar cita"}</p>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={resetForm} data-testid="cancel-edit-appointment" className="text-stonemute hover:text-ink hover:bg-sand">
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="ap-fecha" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Fecha *</Label>
            <Input id="ap-fecha" type="date" required value={form.fecha} onChange={set("fecha")} data-testid="appointment-fecha-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
          <div>
            <Label htmlFor="ap-paciente" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Paciente *</Label>
            <Input id="ap-paciente" required minLength={2} value={form.paciente} onChange={set("paciente")} placeholder="Nombre del paciente" data-testid="appointment-paciente-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Servicio *</Label>
            <Select value={form.servicio} onValueChange={(v) => setForm((f) => ({ ...f, servicio: v }))}>
              <SelectTrigger data-testid="appointment-servicio-select" className="mt-1.5 h-11 bg-sand/50 border-line focus:ring-sage">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent className="bg-white border-line">
                {SERVICIOS.map((s, i) => <SelectItem key={s} value={s} data-testid={`appointment-servicio-option-${i}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ap-monto" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Monto ($) *</Label>
            <Input id="ap-monto" type="number" min="0" step="0.01" required value={form.monto} onChange={set("monto")} placeholder="0.00" data-testid="appointment-monto-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Estado</Label>
            <Select value={form.estado} onValueChange={(v) => setForm((f) => ({ ...f, estado: v }))}>
              <SelectTrigger data-testid="appointment-estado-select" className="mt-1.5 h-11 bg-sand/50 border-line focus:ring-sage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-line">
                <SelectItem value="pagada" data-testid="appointment-estado-pagada">Pagada</SelectItem>
                <SelectItem value="pendiente" data-testid="appointment-estado-pendiente">Pendiente por cobrar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ap-notas" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Notas</Label>
            <Input id="ap-notas" value={form.notas} onChange={set("notas")} placeholder="Opcional" data-testid="appointment-notas-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
        </div>
        <Button type="submit" disabled={saving} data-testid="submit-appointment-form" className="mt-5 rounded-full bg-sage text-white text-sm font-bold hover:bg-sage-dark">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" /> {editingId ? "Guardar cambios" : "Agregar cita"}</span>}
        </Button>
      </form>

      <div className="bg-white border border-line rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <p className="font-serif text-lg">Citas de {monthLabel}</p>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-finances-button" className="rounded-full border-line hover:bg-sand hover:text-ink">
            <Download className="h-4 w-4 mr-2" /> Exportar mes (CSV)
          </Button>
        </div>
        {loading ? (
          <div className="p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sage" /></div>
        ) : monthItems.length === 0 ? (
          <p className="p-10 text-center text-sm text-stonemute" data-testid="empty-appointments-message">No hay citas registradas en este mes.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="appointments-table">
              <TableHeader>
                <TableRow className="border-line">
                  <TableHead>Fecha</TableHead>
                  <TableHead>Paciente</TableHead>
                  <TableHead>Servicio</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Notas</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthItems.map((a) => (
                  <TableRow key={a.id} className="border-line" data-testid={`appointment-row-${a.id}`}>
                    <TableCell className="whitespace-nowrap text-stonemute text-xs">
                      {new Date(`${a.fecha}T12:00:00`).toLocaleDateString("es", { day: "2-digit", month: "short" })}
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap">{a.paciente}</TableCell>
                    <TableCell className="whitespace-nowrap">{a.servicio}</TableCell>
                    <TableCell className="whitespace-nowrap font-semibold">{money(a.monto)}</TableCell>
                    <TableCell>
                      <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${a.estado === "pagada" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid={`appointment-estado-badge-${a.id}`}>
                        {a.estado === "pagada" ? "Pagada" : "Pendiente"}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate text-stonemute">{a.notas || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(a)} data-testid={`edit-appointment-button-${a.id}`} className="text-stonemute hover:text-sage hover:bg-sand">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(a.id)} data-testid={`delete-appointment-button-${a.id}`} className="text-stonemute hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
